import type { AddressFieldValue, AddressSubValue } from '@checkout-page/schemas';
import { getAddressRule } from './addressRules';
import { translateAddressKey } from './addressTranslations';

type AddressFieldConfig = {
  collect?: 'billing' | 'billing_and_shipping';
};

export type AddressValidationError = {
  side: 'billing' | 'shipping';
  missing: string[];
};

function isMissing(v: string | undefined): boolean {
  return !v || v.trim() === '';
}

function missingForSide(sub: AddressSubValue | undefined): string[] {
  if (!sub) return ['country'];
  const missing: string[] = [];
  if (isMissing(sub.country)) missing.push('country');
  const rule = getAddressRule(sub.country || 'US');
  for (const key of rule.required) {
    if (isMissing(sub[key])) missing.push(key);
  }
  return missing;
}

export function validateAddressValue(
  field: AddressFieldConfig,
  value: AddressFieldValue | undefined,
): AddressValidationError[] {
  const isDual = field.collect === 'billing_and_shipping';
  const errors: AddressValidationError[] = [];

  if (isDual) {
    const shippingMissing = missingForSide(value?.shipping);
    if (shippingMissing.length > 0) errors.push({ side: 'shipping', missing: shippingMissing });

    const sameAsShipping = value?.sameAsShipping ?? true;
    if (!sameAsShipping) {
      const billingMissing = missingForSide(value?.billing);
      if (billingMissing.length > 0) errors.push({ side: 'billing', missing: billingMissing });
    }
  } else {
    const billingMissing = missingForSide(value?.billing);
    if (billingMissing.length > 0) errors.push({ side: 'billing', missing: billingMissing });
  }

  return errors;
}

export function formatAddressValidationError(
  errors: AddressValidationError[],
  locale?: string,
): string {
  if (errors.length === 0) return '';
  const hasShipping = errors.some(e => e.side === 'shipping');
  const hasBilling = errors.some(e => e.side === 'billing');
  const key =
    hasShipping && hasBilling
      ? 'validation.both'
      : hasShipping
        ? 'validation.shipping'
        : 'validation.billing';
  return translateAddressKey(locale, key);
}
